/**
 * Status filter state for operational Target Investors (Jason UI lists Phase 2A).
 *
 * Lists are the 15 canonical progress values only, grouped Active then Inactive
 * Active = engaged (Info Request through Invested). Inactive = canonical and
 * not engaged (No Contact, Sent, Contact, Passed, Gone Cold, On Hold).
 * Missing and legacy values are never listed. Inclusive All still matches them (2B wiring).
 */
import {
  TARGET_INVESTOR_PROGRESS_OPTIONS,
  isTargetInvestorActiveProgress,
  isTargetInvestorProgress,
} from './targetInvestorProgress';

export type TargetInvestorStatusFilterRadio = 'all' | 'active' | 'inactive';

export type TargetInvestorStatusFilterState = {
  radio: TargetInvestorStatusFilterRadio;
  selectedKeys: string[];
};

export type AllStatusesCheckState = 'checked' | 'unchecked' | 'indeterminate';

export type StatusFilterOption = { value: string; label: string };

export const ACTIVE_STATUS_OPTIONS: StatusFilterOption[] =
  TARGET_INVESTOR_PROGRESS_OPTIONS.filter((option) =>
    isTargetInvestorActiveProgress(option.value)
  ).map((option) => ({ value: option.value, label: option.label }));

export const INACTIVE_STATUS_OPTIONS: StatusFilterOption[] =
  TARGET_INVESTOR_PROGRESS_OPTIONS.filter(
    (option) => !isTargetInvestorActiveProgress(option.value)
  ).map((option) => ({ value: option.value, label: option.label }));

export const LISTED_STATUS_OPTIONS: StatusFilterOption[] = [
  ...ACTIVE_STATUS_OPTIONS,
  ...INACTIVE_STATUS_OPTIONS,
];

export const ACTIVE_STATUS_VALUES: string[] = ACTIVE_STATUS_OPTIONS.map(
  (option) => option.value
);
export const INACTIVE_STATUS_VALUES: string[] = INACTIVE_STATUS_OPTIONS.map(
  (option) => option.value
);
export const ALL_LISTED_STATUS_VALUES: string[] = LISTED_STATUS_OPTIONS.map(
  (option) => option.value
);

const ALL_LISTED_SET = new Set(ALL_LISTED_STATUS_VALUES);
const LISTED_LABEL_BY_VALUE = new Map(
  LISTED_STATUS_OPTIONS.map((option) => [option.value, option.label])
);

const keysInListedOrder = (keys: Iterable<string>): string[] => {
  const selected = new Set(keys);
  return ALL_LISTED_STATUS_VALUES.filter((value) => selected.has(value));
};

const sameListedSet = (keys: string[], expected: string[]): boolean => {
  if (keys.length !== expected.length) return false;
  const selected = new Set(keys);
  return expected.every((value) => selected.has(value));
};

const inferRadio = (
  keys: string[]
): TargetInvestorStatusFilterRadio | null => {
  if (sameListedSet(keys, ALL_LISTED_STATUS_VALUES)) return 'all';
  if (sameListedSet(keys, ACTIVE_STATUS_VALUES)) return 'active';
  if (sameListedSet(keys, INACTIVE_STATUS_VALUES)) return 'inactive';
  return null;
};

export const createAllStatusFilter = (): TargetInvestorStatusFilterState => ({
  radio: 'all',
  selectedKeys: [...ALL_LISTED_STATUS_VALUES],
});

export const createActiveStatusFilter = (): TargetInvestorStatusFilterState => ({
  radio: 'active',
  selectedKeys: [...ACTIVE_STATUS_VALUES],
});

export const createInactiveStatusFilter = (): TargetInvestorStatusFilterState => ({
  radio: 'inactive',
  selectedKeys: [...INACTIVE_STATUS_VALUES],
});

export const selectStatusFilterRadio = (
  radio: TargetInvestorStatusFilterRadio
): TargetInvestorStatusFilterState => {
  if (radio === 'active') return createActiveStatusFilter();
  if (radio === 'inactive') return createInactiveStatusFilter();
  return createAllStatusFilter();
};

export const isListedStatusValue = (value: string): boolean =>
  ALL_LISTED_SET.has(value);

export const getAllStatusesCheckState = (
  state: TargetInvestorStatusFilterState
): AllStatusesCheckState => {
  const count = state.selectedKeys.filter((value) => ALL_LISTED_SET.has(value))
    .length;
  if (count === 0) return 'unchecked';
  if (count === ALL_LISTED_STATUS_VALUES.length) return 'checked';
  return 'indeterminate';
};

export const isInclusiveAllStatusFilter = (
  state: TargetInvestorStatusFilterState
): boolean =>
  state.radio === 'all' &&
  sameListedSet(state.selectedKeys, ALL_LISTED_STATUS_VALUES);

export const toggleStatusFilterValue = (
  state: TargetInvestorStatusFilterState,
  value: string
): TargetInvestorStatusFilterState => {
  if (!ALL_LISTED_SET.has(value)) return state;
  const selected = new Set(state.selectedKeys);
  if (selected.has(value)) {
    selected.delete(value);
  } else {
    selected.add(value);
  }
  const selectedKeys = keysInListedOrder(selected);
  return {
    radio: inferRadio(selectedKeys) ?? state.radio,
    selectedKeys,
  };
};

export const toggleAllStatuses = (
  state: TargetInvestorStatusFilterState
): TargetInvestorStatusFilterState => {
  if (getAllStatusesCheckState(state) === 'checked') {
    return { radio: 'all', selectedKeys: [] };
  }
  return createAllStatusFilter();
};

export const matchesTargetInvestorStatusFilter = (
  raw: unknown,
  state: TargetInvestorStatusFilterState
): boolean => {
  if (isInclusiveAllStatusFilter(state)) return true;
  if (!isTargetInvestorProgress(raw)) return false;
  return state.selectedKeys.includes(raw);
};

export const getStatusFilterTriggerLabel = (
  state: TargetInvestorStatusFilterState
): string => {
  if (isInclusiveAllStatusFilter(state)) return 'All Statuses';
  if (
    state.radio === 'active' &&
    sameListedSet(state.selectedKeys, ACTIVE_STATUS_VALUES)
  ) {
    return 'Active';
  }
  if (
    state.radio === 'inactive' &&
    sameListedSet(state.selectedKeys, INACTIVE_STATUS_VALUES)
  ) {
    return 'Inactive';
  }
  if (state.selectedKeys.length === 1) {
    return LISTED_LABEL_BY_VALUE.get(state.selectedKeys[0]) ?? 'Status';
  }
  if (state.selectedKeys.length === 0) return 'Status';
  return `${state.selectedKeys.length} statuses`;
};

export const isStatusFilterActive = (
  state: TargetInvestorStatusFilterState
): boolean => !isInclusiveAllStatusFilter(state);
